import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Timeline, TimelineConnector, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab';
import { Box, Divider, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";
import { ReactComponent as Chat } from "../../../assets/icons/message.svg";
import { DashboardContext } from "../../../contexts/DashboardContext";
import useDashboardQuery from "../../../hooks/useDashboardQuery";
import { scrollbarStyle } from '../../../shared/dashboard';
import theme from "../../../theme";
import { EmailBody, UnassignedContentType } from "../../../types/files";
import { EmailSummary } from "../../../types/search";
import ArrayUtils from "../../../utils/ArrayUtils";
import DashboardAssignedContentCard from "../../molecules/dashboard-assigned-content/DashboardAssignedContentCard";
import LinkedInMessage from '../../molecules/dashboard-messages/LinkedInMessage';
import EmailSummaryQuery from "../../molecules/dashboard-queries/EmailSummaryQuery";

const useStyles = makeStyles((theme) => ({
    panel: {
        transition: 'width .3s ease',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        ...scrollbarStyle
    },
    activity: {
        overflowX: 'hidden',
        overflowY: 'auto',
        zIndex: 1300,
        ...scrollbarStyle,
    },
    feed: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    connector : {
        background: theme.colors.neutral['300'],
    },
    content : {
        padding: '0px !important',
        paddingLeft: '24px !important',
    },
}));

const SummaryOfCommunications: React.FC<{}> = () => {
    const [hasSummary, setHasSummary] = useState<boolean>(true);

    return (<>
        <Stack direction="column" spacing={2} alignItems="center" width="100%" height="100%" mt={2} p={2}>
            {hasSummary && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                    <Chat style={{ width: 32, height: 32, stroke: '#7bd4d4' }} />
                    <Typography fontFamily="Inter" fontSize="1.1rem" fontWeight="bold" color="#7bd4d4">
                        {'Summary of communications'}
                    </Typography>
                </Stack>
            )}
            {hasSummary ? (
                <Stack direction="column" spacing={4} alignItems="center" width="100%" height="100%" px={2} py={1}
                    sx={{ maxWidth: 'calc(33vw - 100px)', overflowY: 'auto', ...scrollbarStyle }}>
                    <EmailSummaryQuery onHasSummary={setHasSummary} noTitle />
                </Stack>
            ) : (
                <Stack direction="column" spacing={2} alignItems="center" justifyContent="center" width="100%" height="100%" p={2}>
                    <Chat style={{ width: 60, height: 60, stroke: theme.colors.neutral['400'] }} />
                    <Typography fontFamily="Inter" fontSize="1.2rem" color={theme.colors.neutral['500']}>
                        {'Summary of communications'}
                    </Typography>
                </Stack>
            )}
            <div>&nbsp;</div>
        </Stack>
    </>);
}

const Emails: React.FC<{ emails: (EmailBody & { summary: string })[] | undefined }> = ({ emails }) => {
    const classes = useStyles();

    return (<>
        <Stack direction="column" spacing={2} alignItems="center" width="100%" height="100%" mt={2} p={2}>
            {!!emails?.length && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                    <EmailIcon style={{ width: 36, height: 36, fill: theme.colors.neutral['400'] }} />
                    <Typography fontFamily="Inter" fontSize="1.1rem" fontWeight="bold" color={theme.colors.neutral['500']}>
                        {'Emails'}
                    </Typography>
                </Stack>
            )}
            {!!emails?.length ? (
                <Stack direction="column" className={classes.feed} alignItems="stretch">
                    <Timeline position="right">
                        {emails.map((content, i) => (
                            <TimelineItem key={`activity-feed-116-${i}-` + content.id}>
                                <TimelineSeparator>
                                    <TimelineConnector className={classes.connector} />
                                    <TimelineDot sx={{ background: theme.colors.neutral['300'], BoxShadow: 'unset'}} />
                                    <TimelineConnector className={classes.connector} />
                                </TimelineSeparator>
                                <TimelineOppositeContent className={classes.content}>
                                    <DashboardAssignedContentCard content={content} />
                                </TimelineOppositeContent>
                            </TimelineItem>
                        ))}
                        <TimelineItem>
                            <TimelineSeparator>
                                <Box width={12} />
                                <TimelineConnector className={classes.connector} sx={{ height: 'calc(100vh - 650px)' }} />
                            </TimelineSeparator>
                            <TimelineOppositeContent className={classes.content} />
                        </TimelineItem>
                    </Timeline>
                </Stack>
            ) : (
                <Stack direction="column" spacing={2} alignItems="center" justifyContent="center" width="100%" height="100%" p={2}>
                    <EmailIcon style={{ width: 60, height: 60, fill: theme.colors.neutral['400'] }} />
                    <Typography fontFamily="Inter" fontSize="1.2rem" color={theme.colors.neutral['500']}>
                        {'No emails yet.'}
                    </Typography>
                </Stack>
            )}
            <div>&nbsp;</div>
        </Stack>
    </>);
}

const Messages: React.FC<{ messages: (EmailBody & {threads: ({ timestamp: string|null, sender?: string, message: string })[]})[] | undefined }> = ({ messages }) => {
    return (<>
        <Stack direction="column" spacing={2} alignItems="center" width="100%" height="100%" mt={2} p={2}>
            {!!messages?.length && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                    <LinkedInIcon style={{ width: 28, height: 28, fill: theme.colors.neutral['400'] }} />
                    <Typography fontFamily="Inter" fontSize="1.1rem" fontWeight="bold" color={theme.colors.neutral['500']}>
                        {'Messages'}
                    </Typography>
                </Stack>
            )}
            {!!messages?.length ? (
                <Stack direction="column" spacing={4} alignItems="center" width="100%" height="100%" p={1}
                    sx={{ overflowY: 'auto', ...scrollbarStyle }}>
                    {messages.map((content, i) =>
                        <LinkedInMessage
                            content={content}
                            key={'dashboard-content-110-' + i}
                        />
                    )}
                </Stack>
            ) : (
                <Stack direction="column" spacing={2} alignItems="center" justifyContent="center" width="100%" height="100%" p={2}>
                    <LinkedInIcon style={{ width: 50, height: 50, fill: theme.colors.neutral['400'] }} />
                    <Typography fontFamily="Inter" fontSize="1.2rem" color={theme.colors.neutral['500']}>
                        {'No messages yet.'}
                    </Typography>
                </Stack>
            )}
            <div>&nbsp;</div>
        </Stack>
    </>);
}

const DashboardCommunications: React.FC<{}> = () => {
    const classes = useStyles();
    const { dashboard } = useContext(DashboardContext);
    const { queryAnswer } = useDashboardQuery('Email Summaries');

    const filterEmails = useCallback((emailBody: EmailBody) => {
        return (!emailBody?.type || ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || ''))
    }, []);

    const emails: (EmailBody & {summary : string})[]|undefined = useMemo(() => {
        const summaries = queryAnswer?.answeredQuestion.answer as EmailSummary[];

        return ArrayUtils.sortByDescending(
            dashboard?.emailBodies?.filter(filterEmails)
                ?.map(emailBody => ({...emailBody,
                    summary: summaries?.find((summary: {id: string|null}) => summary?.id === emailBody?.id)?.summary || ''}))
                ?? []
        , 'createdAt');
    }, [dashboard?.emailBodies, queryAnswer?.answeredQuestion, filterEmails]);

    const messages: (EmailBody & {threads: ({ timestamp: string|null, sender?: string, message: string })[]})[]|undefined = useMemo(() => {
        return ArrayUtils.sortByDescending(
            dashboard?.emailBodies?.filter(emailBody => emailBody?.type === UnassignedContentType.LINKEDIN_MESSAGE)
                ?.map(emailBody => ({...emailBody,
                    threads: emailBody?.content?.split('\n')
                        .map((msgBlock: string) => JSON.parse(
                            msgBlock.replace(/^\uFEFF/, "")
                            // eslint-disable-next-line
                            .replace(/[\x00-\x1F\x7F]/g, "")
                            .replace(/-\s*(?![0-9])/g, "")
                            .trim() || ''))
                })) ?? []
        , 'createdAt');
    }, [dashboard?.emailBodies]);

    if (!dashboard)
        return (<></>);

    return (<>
        <Box className={classes.panel}>
            <Box padding="12px 0" />
            <Stack direction="row" alignItems="center" justifyContent="flex-start"
                width="100%" height="100%" sx={{ overflowX: 'auto', overflowY: 'hidden', ...scrollbarStyle }}>
                <SummaryOfCommunications />
                <Divider orientation="vertical" />
                <Emails emails={emails} />
                <Divider orientation="vertical" />
                <Messages messages={messages} />
            </Stack>
        </Box>
    </>);
}

export default DashboardCommunications;